import { gql, useMutation, useQuery } from "@apollo/client";
import { useCallback } from "react";
import { SET_LOCATION_SECTION_RESPONSIBILITIES } from "@/graphql/mutations";
import type { MatrixData } from "@/lib/responsibilityMatrix";

const GET_RESPONSIBILITY_MATRIX = gql`
  query GetResponsibilityMatrix($projectId: String!) {
    responsibilityMatrix(projectId: $projectId) {
      sections {
        sectionId
        code
        name
        plotId
        headsectionNumber
        headsectionLabel
        defaultResponsibleUserId
      }
      rules {
        locationId
        sectionId
        responsibleUserId
      }
      sectionLocationResponsibles {
        locationId
        sectionId
        responsibleUserId
      }
    }
  }
`;

export interface MatrixCell {
  locationId: string;
  sectionId: string;
}

export function useResponsibilityMatrix(projectId: string | null) {
  const { data, loading, error, refetch } = useQuery<{ responsibilityMatrix: MatrixData }>(
    GET_RESPONSIBILITY_MATRIX,
    { variables: { projectId }, skip: !projectId, fetchPolicy: "cache-and-network" },
  );
  const [mutate] = useMutation(SET_LOCATION_SECTION_RESPONSIBILITIES);

  const assign = useCallback(
    async (cells: MatrixCell[], userId: string | null) => {
      const res = await mutate({
        variables: { input: { projectId, cells, responsibleUserId: userId } },
      });
      const result = res.data?.setLocationSectionResponsibilities;
      if (!result?.success) throw new Error(result?.message ?? "Tallennus epäonnistui");
      await refetch();
    },
    [mutate, projectId, refetch],
  );

  return {
    data: data?.responsibilityMatrix ?? null,
    loading,
    error: error?.message ?? null,
    assign,
  };
}
